import { Helmet } from "react-helmet-async";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { MapPin, Phone } from "lucide-react";
import InstantQuoteCTA from "@/components/InstantQuoteCTA";

const ServiceArea = () => {
  return (
    <>
      <Helmet>
        <title>Service Area | Rick's Black Car Service San Antonio</title>
        <meta 
          name="description" 
          content="Rick's Black Car Service serves San Antonio, Stone Oak, Alamo Heights, Boerne & New Braunfels with premium luxury transportation." 
        />
        <link rel="canonical" href="https://ricksblackcarservicesantonio.com/service-area/" />
        <meta property="og:title" content="Service Area - Rick's Black Car Service" />
        <meta 
          property="og:description" 
          content="Rick's Black Car Service serves San Antonio, Stone Oak, Alamo Heights, Boerne & New Braunfels with premium luxury transportation." 
        />
        <meta property="og:url" content="https://ricksblackcarservicesantonio.com/service-area" />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Navbar />
        
        {/* Hero Section */}
        <section className="pt-32 pb-16 bg-gradient-to-b from-background to-muted/20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-6">
                <MapPin className="w-4 h-4" />
                <span className="text-sm font-medium">Serving San Antonio & Beyond</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                Areas We Serve
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Rick's Black Car Service provides luxury transportation throughout San Antonio and the surrounding Hill Country region.
              </p>
            </div>
          </div>
        </section>

        {/* Primary Service Area */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
                Primary Service Area: San Antonio, TX
              </h2>

              <div className="grid md:grid-cols-3 gap-8 mb-16">
                {/* North San Antonio */}
                <div className="bg-card border border-border rounded-lg p-8 hover:shadow-lg transition-shadow">
                  <h3 className="text-2xl font-bold mb-6 text-primary">North San Antonio</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <MapPin className="w-5 h-5 text-primary mr-2 flex-shrink-0 mt-1" />
                      <span>Stone Oak</span>
                    </li>
                    <li className="flex items-start">
                      <MapPin className="w-5 h-5 text-primary mr-2 flex-shrink-0 mt-1" />
                      <span>The Dominion</span>
                    </li>
                    <li className="flex items-start">
                      <MapPin className="w-5 h-5 text-primary mr-2 flex-shrink-0 mt-1" />
                      <span>Shavano Park</span>
                    </li>
                    <li className="flex items-start">
                      <MapPin className="w-5 h-5 text-primary mr-2 flex-shrink-0 mt-1" />
                      <span>Castle Hills</span>
                    </li>
                    <li className="flex items-start">
                      <MapPin className="w-5 h-5 text-primary mr-2 flex-shrink-0 mt-1" />
                      <span>Hollywood Park</span>
                    </li>
                  </ul>
                </div>

                {/* Central San Antonio */}
                <div className="bg-card border border-border rounded-lg p-8 hover:shadow-lg transition-shadow">
                  <h3 className="text-2xl font-bold mb-6 text-primary">Central San Antonio</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <MapPin className="w-5 h-5 text-primary mr-2 flex-shrink-0 mt-1" />
                      <span>Alamo Heights</span>
                    </li>
                    <li className="flex items-start">
                      <MapPin className="w-5 h-5 text-primary mr-2 flex-shrink-0 mt-1" />
                      <span>Terrell Hills</span>
                    </li>
                    <li className="flex items-start">
                      <MapPin className="w-5 h-5 text-primary mr-2 flex-shrink-0 mt-1" />
                      <span>Olmos Park</span>
                    </li>
                    <li className="flex items-start">
                      <MapPin className="w-5 h-5 text-primary mr-2 flex-shrink-0 mt-1" />
                      <span>Downtown San Antonio</span>
                    </li>
                    <li className="flex items-start">
                      <MapPin className="w-5 h-5 text-primary mr-2 flex-shrink-0 mt-1" />
                      <span>Medical Center</span>
                    </li>
                    <li className="flex items-start">
                      <MapPin className="w-5 h-5 text-primary mr-2 flex-shrink-0 mt-1" />
                      <span>UTSA Area</span>
                    </li>
                  </ul>
                </div>

                {/* Airport Area */}
                <div className="bg-card border border-border rounded-lg p-8 hover:shadow-lg transition-shadow">
                  <h3 className="text-2xl font-bold mb-6 text-primary">Airport Area</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <MapPin className="w-5 h-5 text-primary mr-2 flex-shrink-0 mt-1" />
                      <span>San Antonio International Airport (SAT)</span>
                    </li>
                    <li className="flex items-start">
                      <MapPin className="w-5 h-5 text-primary mr-2 flex-shrink-0 mt-1" />
                      <span>Airport vicinity hotels</span>
                    </li>
                    <li className="flex items-start">
                      <MapPin className="w-5 h-5 text-primary mr-2 flex-shrink-0 mt-1" />
                      <span>Airport business district</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Extended Service Areas */}
        <section className="py-16 bg-muted/20">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
                Extended Service Areas
              </h2>
              <p className="text-lg text-muted-foreground text-center mb-12 max-w-3xl mx-auto">
                We also provide service to these surrounding cities:
              </p>

              <div className="grid md:grid-cols-3 gap-6 mb-16">
                {[
                  "Boerne, TX",
                  "New Braunfels, TX",
                  "Schertz, TX",
                  "Cibolo, TX",
                  "Seguin, TX",
                  "San Marcos, TX",
                  "Canyon Lake, TX",
                  "Bulverde, TX",
                  "Fair Oaks Ranch, TX"
                ].map((city) => (
                  <div 
                    key={city} 
                    className="bg-card border border-border rounded-lg p-6 hover:shadow-md transition-shadow flex items-center"
                  >
                    <MapPin className="w-5 h-5 text-primary mr-3 flex-shrink-0" />
                    <span className="font-medium">{city}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Special Requests */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="bg-card border border-border rounded-lg p-8 md:p-12">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Special Requests
                </h2>
                <p className="text-lg text-muted-foreground mb-8">
                  Need transportation to Austin, Houston, or other Texas cities? We accommodate special requests for longer-distance travel. Contact us for custom quotes.
                </p>
                
                <div className="bg-primary/10 border border-primary/20 rounded-lg p-6">
                  <h3 className="text-xl font-bold mb-3 text-primary">Coverage Map</h3>
                  <p className="text-muted-foreground">
                    Our service radius extends approximately 50 miles from San Antonio, ensuring reliable luxury transportation throughout the greater metro area.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-b from-background to-muted/20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Ready to Experience Premium Transportation?
              </h2>
              <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                Whether you're in San Antonio or any of our surrounding service areas, Rick's Black Car Service is ready to provide exceptional luxury transportation.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button className="btn-luxury" size="lg" asChild>
                  <Link to="/contact">Book Your Ride</Link>
                </Button>
                <Button variant="outline" size="lg" asChild>
                  <a href="tel:+12102967409" className="flex items-center">
                    <Phone className="w-4 h-4 mr-2" />
                    210-296-7409
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Instant Quote CTA */}
        <InstantQuoteCTA />

        <Footer />
      </div>
    </>
  );
};

export default ServiceArea;
